/**
 * Canonical skill progress utilities.
 *
 * This is the single source of truth for checking item/qualification
 * completion, computing progress percentages, and determining
 * qualification status. All components should use these functions
 * rather than implementing their own checks.
 *
 * Key concept: a JQR item may have retry entries in progress data.
 * The base key is `${qualId}_${itemId}`, and retries are stored as
 * `${qualId}_${itemId}_retry_1`, `_retry_2`, etc. An item is
 * considered complete if the base OR any retry has `completed: true`.
 */

import type { Qualification, Progress, RubricGrades } from '../types';

// ── Rubric checks ──

/** Check if rubric scores indicate a fail: any score of 1, or more than one score of 2 */
export const isRubricFail = (rubric: RubricGrades | null): boolean => {
  if (!rubric) return false;
  const scores = Object.values(rubric).filter((v): v is number => v !== null);
  if (scores.length === 0) return false;
  if (scores.some((s) => s === 1)) return true;
  if (scores.filter((s) => s === 2).length > 1) return true;
  return false;
};

// ── Item completion ──

const MAX_RETRIES = 20;

/** Check if a single JQR item is completed, accounting for retries */
export const isItemCompleted = (
  progress: Record<string, Progress>,
  qualId: string,
  itemId: string,
): boolean => {
  const baseKey = `${qualId}_${itemId}`;
  if (progress[baseKey]?.completed) return true;
  for (let r = 1; r <= MAX_RETRIES; r++) {
    const retryKey = `${baseKey}_retry_${r}`;
    const retry = progress[retryKey];
    if (!retry) break;
    if (retry.completed) return true;
  }
  return false;
};

// ── Retry helpers ──

/** Count how many retry entries exist for an item */
export const getRetryCount = (
  progress: Record<string, Progress>,
  qualId: string,
  itemId: string,
): number => {
  let count = 0;
  for (let r = 1; r <= MAX_RETRIES; r++) {
    if (!progress[`${qualId}_${itemId}_retry_${r}`]) break;
    count = r;
  }
  return count;
};

/**
 * Determine how many retry cards to show for an item.
 * Shows retries only if the base item has a rubric failure.
 * Shows an extra blank retry slot if the last existing retry also failed.
 */
export const getRetriesNeeded = (
  progress: Record<string, Progress>,
  qualId: string,
  itemId: string,
): number => {
  const baseKey = `${qualId}_${itemId}`;
  const baseProgress = progress[baseKey];
  if (!baseProgress || !isRubricFail(baseProgress.rubric || null)) return 0;

  const existingRetries = getRetryCount(progress, qualId, itemId);
  if (existingRetries === 0) return 1;

  const lastRetryKey = `${baseKey}_retry_${existingRetries}`;
  const lastRetryProgress = progress[lastRetryKey];
  if (lastRetryProgress && isRubricFail(lastRetryProgress.rubric || null)) {
    return existingRetries + 1;
  }
  return existingRetries;
};

// ── Qualification-level stats ──

/** Count completed items in a qualification */
export const getCompletedCount = (
  qual: Qualification,
  progress: Record<string, Progress>,
): number => {
  return qual.items.filter((item) => isItemCompleted(progress, qual.id, item.id)).length;
};

/** Get completion percentage for a qualification (0–100) */
export const getProgressPercentage = (
  qual: Qualification,
  progress: Record<string, Progress>,
): number => {
  if (qual.items.length === 0) return 0;
  return Math.round((getCompletedCount(qual, progress) / qual.items.length) * 100);
};

/** Determine the status of a qualification based on prerequisites and progress */
export type QualStatus = 'locked' | 'available' | 'in-progress' | 'complete';

export const getQualStatus = (
  qual: Qualification,
  allQuals: Qualification[],
  progress: Record<string, Progress>,
): QualStatus => {
  // Check prerequisites recursively
  const allPrereqsMet = qual.prerequisites.every((prereqId) => {
    const prereqQual = allQuals.find((q) => q.id === prereqId);
    if (!prereqQual) return true;
    return getQualStatus(prereqQual, allQuals, progress) === 'complete';
  });

  if (!allPrereqsMet) return 'locked';
  if (qual.items.length === 0) return 'available';

  const completed = getCompletedCount(qual, progress);
  if (completed === 0) return 'available';
  if (completed === qual.items.length) return 'complete';
  return 'in-progress';
};

// ── Tree-level stats ──

/** Compute overall progress for a set of qualifications (e.g. a skill tree) */
export const getTreeProgress = (
  quals: Qualification[],
  progress: Record<string, Progress>,
): { completed: number; total: number; percentage: number } => {
  let completed = 0;
  let total = 0;
  for (const qual of quals) {
    total += qual.items.length;
    completed += getCompletedCount(qual, progress);
  }
  return {
    completed,
    total,
    percentage: total === 0 ? 0 : Math.round((completed / total) * 100),
  };
};
